import { EventType } from '@rrweb/types'
import { Duration } from 'luxon'
import {
  Activity,
  MonitoringSpecialistAssignmentOutdoorActivityType,
  ProcessedActivity,
  ProcessedActivityAddons,
  SystemActorType
} from '../../../api/models'
import { MISSING_VALUE } from '../../../constants'
import { ProcessedRRWebEvents } from './rrweb/types'
import { RRWEB_EVENTS } from '@simplisafe/compass-web'

export type DeadSpaceRange = {
  relativeStartTimeSeconds: number
  relativeEndTimeSeconds: number
}

/**
 * Derives "dead space" (no video) time ranges as the gaps between a RECORDING_STOPPED event and the
 * next RECORDING_STARTED event.
 */
export function getDeadSpaceRanges(
  processedRRWebEvents: Pick<ProcessedRRWebEvents, 'recordingStartedOrStoppedEvents' | 'timeOfFirstEvent' | 'timeOfLastEvent'>
): DeadSpaceRange[] {
  const { recordingStartedOrStoppedEvents, timeOfFirstEvent, timeOfLastEvent } = processedRRWebEvents

  const sortedEvents = [...recordingStartedOrStoppedEvents].sort((a, b) => a.timestamp - b.timestamp)

  const ranges: DeadSpaceRange[] = []
  let stoppedAtTimestamp: number | undefined

  for (const event of sortedEvents) {
    if (event.type !== EventType.Custom) continue

    if (event.data.tag === RRWEB_EVENTS.RECORDING_STOPPED) {
      stoppedAtTimestamp = event.timestamp
    } else if (event.data.tag === RRWEB_EVENTS.RECORDING_STARTED && stoppedAtTimestamp !== undefined) {
      const start = Math.max(stoppedAtTimestamp, timeOfFirstEvent)
      const end = Math.min(event.timestamp, timeOfLastEvent)

      if (end > start) {
        ranges.push({
          relativeStartTimeSeconds: (start - timeOfFirstEvent) / 1000,
          relativeEndTimeSeconds: (end - timeOfFirstEvent) / 1000
        })
      }
      stoppedAtTimestamp = undefined
    }
  }

  return ranges
}

/**
 * @param processedActivities
 * @param range
 */
export const findActivityInDeadSpaceRange = (processedActivities: ProcessedActivity[], range: DeadSpaceRange) =>
  processedActivities.find(
    activity =>
      activity.hasVideo === false &&
      activity.relativeStartTimeSeconds < range.relativeEndTimeSeconds &&
      activity.relativeEndTimeSeconds > range.relativeStartTimeSeconds
  )

// True only when the activity's ENTIRE time range falls inside a single dead-space range — i.e.
// there's nothing to show for this activity at all (it typically renders as a black screen).
function isFullyDeadSpace(relativeStartTimeSeconds: number, relativeEndTimeSeconds: number, deadSpaceRanges: DeadSpaceRange[]): boolean {
  return deadSpaceRanges.some(
    range => range.relativeStartTimeSeconds <= relativeStartTimeSeconds && relativeEndTimeSeconds <= range.relativeEndTimeSeconds
  )
}

export const PRIMARILY_DEAD_SPACE_RATIO = 0.9

function getDeadSpaceOverlapSeconds(relativeStartTimeSeconds: number, relativeEndTimeSeconds: number, deadSpaceRanges: DeadSpaceRange[]): number {
  return deadSpaceRanges.reduce((totalOverlapSeconds, range) => {
    const overlapStart = Math.max(relativeStartTimeSeconds, range.relativeStartTimeSeconds)
    const overlapEnd = Math.min(relativeEndTimeSeconds, range.relativeEndTimeSeconds)
    return totalOverlapSeconds + Math.max(0, overlapEnd - overlapStart)
  }, 0)
}

// Due to slight timing discrepancies we cannot always expect a 100% overlap,
// so we use a threshold to determine if the activity is at least 90% dead space.
function isPrimarilyDeadSpace(relativeStartTimeSeconds: number, relativeEndTimeSeconds: number, deadSpaceRanges: DeadSpaceRange[]): boolean {
  const durationSeconds = relativeEndTimeSeconds - relativeStartTimeSeconds
  if (durationSeconds <= 0) {
    return isFullyDeadSpace(relativeStartTimeSeconds, relativeEndTimeSeconds, deadSpaceRanges)
  }
  const overlapSeconds = getDeadSpaceOverlapSeconds(relativeStartTimeSeconds, relativeEndTimeSeconds, deadSpaceRanges)
  return overlapSeconds / durationSeconds >= PRIMARILY_DEAD_SPACE_RATIO
}

// processActivities only needs the recording's first/last event times plus the recording start/stop
// events (for dead-space detection) - not the full processed event stream. Declaring just that lets
// callers without the heavy rrweb pipeline (e.g. ErtInspect) supply those directly. A full
// ProcessedRRWebEvents still satisfies this.
type RRWebActivityTimeBounds = Pick<ProcessedRRWebEvents, 'timeOfFirstEvent' | 'timeOfLastEvent' | 'recordingStartedOrStoppedEvents'>

const SYSTEM_ACTOR_ACTIVITY_ALLOWLIST: ReadonlySet<string> = new Set([
  MonitoringSpecialistAssignmentOutdoorActivityType.MonitoringSpecialistAssignment
])

export const formatActivityCardTime = (seconds = 0) => {
  const formattedTime = Duration.fromObject({ seconds: Math.abs(seconds) })
    .shiftTo('minutes', 'seconds')
    .toFormat('mm:ss')

  return seconds < 0 ? `-${formattedTime}` : formattedTime
}

export const formatActivityCardTimeWithMs = (milliseconds = 0) => {
  const formattedTime = Duration.fromObject({ milliseconds: Math.abs(milliseconds) })
    .shiftTo('minutes', 'seconds', 'milliseconds')
    .toFormat('mm:ss.SSS')

  return milliseconds < 0 ? `-${formattedTime}` : formattedTime
}

function calculateProcessedActivityAddons(
  activityPosition: number,
  processedRRWebEvents: RRWebActivityTimeBounds,
  activity: Activity,
  nextActivity?: Activity
): Omit<ProcessedActivityAddons, 'hasVideo' | 'isPrimarilyDeadSpace'> {
  const activityStartTimestamp = activity.started?.timestamp ?? activity.created.timestamp
  const absoluteStartTime = new Date(activityStartTimestamp).getTime()

  const activityEndTimestamp =
    activity.completed?.timestamp || nextActivity?.started?.timestamp || nextActivity?.created.timestamp || activityStartTimestamp
  let absoluteEndTime = new Date(activityEndTimestamp).getTime()

  const nextActivityStartTimestamp = nextActivity?.started?.timestamp || nextActivity?.created.timestamp
  const nextActivityStartTime = nextActivityStartTimestamp ? new Date(nextActivityStartTimestamp).getTime() : null

  if (nextActivityStartTime && absoluteEndTime > nextActivityStartTime) {
    absoluteEndTime = nextActivityStartTime
  }

  let durationMillis = absoluteEndTime - absoluteStartTime
  durationMillis = durationMillis >= 0 ? durationMillis : 0

  const relativeStartTimeMillis = absoluteStartTime - processedRRWebEvents.timeOfFirstEvent
  const relativeEndTimeMillis = absoluteEndTime - processedRRWebEvents.timeOfFirstEvent

  const timesObservedByTheServer = {
    durationMillis,
    durationSeconds: durationMillis / 1000,

    absoluteStartTime,
    relativeStartTimeMillis,
    relativeStartTimeSeconds: relativeStartTimeMillis / 1000,

    absoluteEndTime,
    relativeEndTimeMillis,
    relativeEndTimeSeconds: relativeEndTimeMillis / 1000
  } satisfies Omit<ProcessedActivityAddons, 'hasVideo' | 'isPrimarilyDeadSpace'>

  return getAdjustedForServerDiscrepancy(timesObservedByTheServer, activityPosition, processedRRWebEvents, activity, nextActivity)
}

function getAdjustedForServerDiscrepancy(
  timesObservedByTheServer: Omit<ProcessedActivityAddons, 'adjustedForServerDiscrepancy' | 'hasVideo' | 'isPrimarilyDeadSpace'>,
  activityPosition: number,
  processedRRWebEvents: RRWebActivityTimeBounds,
  activity: Activity,
  nextActivity?: Activity
): Omit<ProcessedActivityAddons, 'adjustedForServerDiscrepancy' | 'hasVideo' | 'isPrimarilyDeadSpace'> {
  let { absoluteStartTime, absoluteEndTime } = timesObservedByTheServer

  if (activityPosition === 0 && activity.type === MonitoringSpecialistAssignmentOutdoorActivityType.MonitoringSpecialistAssignment) {
    // special handling for the first activity that when it is a MonitoringSpecialistAssignment because we want it to always be treated as a preroll activity
    if (absoluteStartTime > processedRRWebEvents.timeOfFirstEvent) {
      absoluteStartTime = processedRRWebEvents.timeOfFirstEvent
    }
    if (absoluteEndTime > processedRRWebEvents.timeOfFirstEvent) {
      absoluteEndTime = processedRRWebEvents.timeOfFirstEvent
    }
  } else if (activityPosition === 1) {
    // if it is the second activity always make its start match the recording start
    absoluteStartTime = processedRRWebEvents.timeOfFirstEvent
  }

  // bound the activities to the rrweb start and end times
  if (absoluteStartTime < processedRRWebEvents.timeOfFirstEvent) {
    absoluteStartTime = processedRRWebEvents.timeOfFirstEvent
  }

  if (absoluteEndTime > processedRRWebEvents.timeOfLastEvent) {
    absoluteEndTime = processedRRWebEvents.timeOfLastEvent
  }

  // if this is the last activity, then make sure it ends at the same time as the rrweb recording
  if (!nextActivity) {
    absoluteEndTime = processedRRWebEvents.timeOfLastEvent
    if (absoluteStartTime > absoluteEndTime) {
      absoluteEndTime = absoluteStartTime
    }
  }

  const relativeStartTimeMillis = absoluteStartTime - processedRRWebEvents.timeOfFirstEvent
  const relativeEndTimeMillis = absoluteEndTime - processedRRWebEvents.timeOfFirstEvent

  let durationMillis = absoluteEndTime - absoluteStartTime
  durationMillis = durationMillis >= 0 ? durationMillis : 0

  return {
    durationMillis,
    durationSeconds: durationMillis / 1000,

    absoluteStartTime,
    relativeStartTimeMillis,
    relativeStartTimeSeconds: relativeStartTimeMillis / 1000,

    absoluteEndTime,
    relativeEndTimeMillis,
    relativeEndTimeSeconds: relativeEndTimeMillis / 1000
  }
}

export function processActivities(processedRRWebEvents: RRWebActivityTimeBounds, activities: Activity[] | undefined): ProcessedActivity[] {
  const reversedActivities: Activity[] = [...(activities ?? [])].reverse()

  const filteredActivities = reversedActivities.filter(activity => {
    const isAssignedToSystemActor =
      activity.assigned !== undefined && 'type' in activity.assigned.assignedTo && activity.assigned.assignedTo.type === SystemActorType.System

    return !isAssignedToSystemActor || SYSTEM_ACTOR_ACTIVITY_ALLOWLIST.has(activity.type)
  })

  const deadSpaceRanges = getDeadSpaceRanges(processedRRWebEvents)
  const processedActivities: ProcessedActivity[] = []

  for (let i = 0; i < filteredActivities.length; i++) {
    const activity = filteredActivities[i]
    const nextActivity = i < filteredActivities.length - 1 ? filteredActivities[i + 1] : undefined
    const activityAddons = calculateProcessedActivityAddons(i, processedRRWebEvents, activity, nextActivity)
    const hasVideo = !isFullyDeadSpace(activityAddons.relativeStartTimeSeconds, activityAddons.relativeEndTimeSeconds, deadSpaceRanges)
    const isSegmentPrimarilyDeadSpace = isPrimarilyDeadSpace(
      activityAddons.relativeStartTimeSeconds,
      activityAddons.relativeEndTimeSeconds,
      deadSpaceRanges
    )
    processedActivities.push({
      ...activity,
      ...activityAddons,
      hasVideo,
      isPrimarilyDeadSpace: isSegmentPrimarilyDeadSpace
    } satisfies ProcessedActivity)
  }

  return processedActivities
}

export function camelCaseToSplitWords(camelCaseString?: string): string {
  if (!camelCaseString) return MISSING_VALUE
  return camelCaseString.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
}

/**
 * An engagement with no recorded configuration predates the escalation-only SOP concept entirely, so
 * `undefined` defaults to 'Standard' — the Current SOP is the only one that existed at the time, not a guess.
 */
export function governingSopDisplayName(isTwoWayAudioEscalationOnly: boolean | undefined): string {
  return isTwoWayAudioEscalationOnly ? 'Escalation Only' : 'Standard'
}
